import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';
import { formatCents } from '@pirouette/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Empty } from '@/components/ui/Empty';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  type DateRange,
  useAccountBalanceAnalytics,
  useAttendanceAnalytics,
  useChargeAnalytics,
  useClassLabels,
  useDanceCastAnalytics,
  useEnrollmentAnalytics,
  useLeadAnalytics,
  usePaymentAnalytics,
  useTrialAnalytics,
} from '@/data/reports';
import {
  agingBuckets,
  arFromBalances,
  atRiskStudents,
  attendanceRate,
  dancesMissingCast,
  enrollmentSnapshot,
  enrollmentTrend,
  formatRate,
  repeatAbsenceGroups,
  retentionSummary,
  revenueSummary,
  trialFunnel,
  type AttendanceRow,
} from '@/lib/analytics';

// Owner analytics dashboard (Phase 4). The /admin parent route already gates
// on the `admin` role server-side, so this child route inherits that guard —
// no extra check needed here (matches every other admin.* route).
export const Route = createFileRoute('/admin/dashboard')({
  component: OwnerAnalyticsDashboard,
});

// ---------------------------------------------------------------------------
// CSV export — mirrors the pattern in admin.reports.index. Kept local because
// the source helper there is not exported; this surface owns its own export.
// ---------------------------------------------------------------------------

type CsvValue = string | number | null;

function toCsvValue(value: CsvValue): string {
  if (value === null) return '';
  const text = String(value);
  if (!/[",\r\n]/.test(text)) return text;
  return `"${text.replaceAll('"', '""')}"`;
}

function exportCsv(filename: string, header: string[], rows: CsvValue[][]) {
  const headerLine = header.map(toCsvValue).join(',');
  const body = rows.map((row) => row.map(toCsvValue).join(',')).join('\n');
  const csv = [headerLine, body].filter(Boolean).join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Date range presets
// ---------------------------------------------------------------------------

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function rangeForDays(days: number): DateRange {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { start: isoDay(start), end: isoDay(end) };
}

const PRESETS: { label: string; days: number }[] = [
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '12 months', days: 365 },
];

// ---------------------------------------------------------------------------
// Layout primitives
// ---------------------------------------------------------------------------

function StatCard({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) {
  return (
    <article className="surface-elevated p-5">
      <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">{label}</p>
      <p className="mt-2 font-[var(--font-display)] text-[var(--text-3xl)] tabular-nums">{value}</p>
      {hint && <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">{hint}</p>}
    </article>
  );
}

function Section({
  id,
  title,
  description,
  action,
  children,
}: {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={`${id}-heading`} className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id={`${id}-heading`} className="text-[var(--text-xl)]">
            {title}
          </h2>
          <p className="mt-1 max-w-3xl text-[var(--color-ink-muted)] text-[var(--text-sm)]">
            {description}
          </p>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function NotEnoughData({ what }: { what: string }) {
  return (
    <Empty
      title="Not enough data yet"
      description={`There isn't enough ${what} in this window to compute a meaningful figure. As records accumulate, this section will populate automatically.`}
    />
  );
}

function SimpleTable({ columns, rows }: { columns: string[]; rows: ReactNode[][] }) {
  return (
    <div className="surface overflow-x-auto">
      <table className="min-w-full text-left text-[var(--text-sm)]">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            {columns.map((c) => (
              <th key={c} className="px-4 py-3 font-medium">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-[var(--color-border)] last:border-0">
              {row.map((cell, j) => (
                <td key={j} className="px-4 py-3 align-top">
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Num({ value }: { value: number }) {
  return <span className="font-medium tabular-nums">{value}</span>;
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

function OwnerAnalyticsDashboard() {
  const [range, setRange] = useState<DateRange>(() => rangeForDays(90));

  const enrollments = useEnrollmentAnalytics(range);
  const charges = useChargeAnalytics(range);
  const payments = usePaymentAnalytics(range);
  const balances = useAccountBalanceAnalytics();
  const attendance = useAttendanceAnalytics(range);
  const classLabels = useClassLabels();
  const leads = useLeadAnalytics(range);
  const trials = useTrialAnalytics(range);
  const danceCast = useDanceCastAnalytics();

  const today = isoDay(new Date());

  // --- Enrollment ---------------------------------------------------------
  const snapshot = useMemo(
    () => enrollmentSnapshot(enrollments.data ?? [], today),
    [enrollments.data, today],
  );
  const trendMonthly = useMemo(
    () => enrollmentTrend(enrollments.data ?? [], 'month'),
    [enrollments.data],
  );
  const retention = useMemo(
    () =>
      range.start && range.end
        ? retentionSummary(enrollments.data ?? [], range.start, range.end)
        : null,
    [enrollments.data, range.start, range.end],
  );

  // --- Revenue / AR -------------------------------------------------------
  const revenue = useMemo(
    () => revenueSummary(charges.data ?? [], payments.data ?? [], today),
    [charges.data, payments.data, today],
  );
  const ar = useMemo(() => arFromBalances(balances.data ?? []), [balances.data]);
  const aging = useMemo(() => agingBuckets(charges.data ?? [], today), [charges.data, today]);

  // --- Attendance ---------------------------------------------------------
  const attendanceRows = useMemo<AttendanceRow[]>(() => {
    const labels = classLabels.data ?? {};
    return (attendance.data ?? []).map((r) => ({
      ...r,
      ...(r.group_id ? { group_label: labels[r.group_id] ?? r.group_id } : {}),
    }));
  }, [attendance.data, classLabels.data]);
  const attRate = useMemo(() => attendanceRate(attendanceRows), [attendanceRows]);
  const repeatAbsence = useMemo(() => repeatAbsenceGroups(attendanceRows, 2), [attendanceRows]);
  const atRisk = useMemo(() => atRiskStudents(attendanceRows, 0.3, 3), [attendanceRows]);

  // --- Trial funnel -------------------------------------------------------
  const funnel = useMemo(
    () => trialFunnel(leads.data ?? [], trials.data ?? []),
    [leads.data, trials.data],
  );

  // --- Recital ops --------------------------------------------------------
  const missingCast = useMemo(() => dancesMissingCast(danceCast.data ?? []), [danceCast.data]);

  const anyError = [
    enrollments.error,
    charges.error,
    payments.error,
    balances.error,
    attendance.error,
    leads.error,
    trials.error,
    danceCast.error,
  ].some(Boolean);

  return (
    <div className="space-y-10">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            Studio intelligence
          </p>
          <h1 className="mt-2">Owner Dashboard</h1>
        </div>
        <DateFilter range={range} onChange={setRange} />
      </header>

      {anyError ? (
        <Empty
          title="Some analytics could not load"
          description="One or more sections failed to load for this session. Refresh to retry; partial data below reflects only what loaded."
        />
      ) : null}

      {/* Enrollment trend ----------------------------------------------- */}
      <Section
        id="enrollment"
        title="Enrollment"
        description="Active headcount today plus per-month starts, drops, and net change across the selected window."
        action={
          trendMonthly.length > 0 ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                exportCsv(
                  'enrollment-trend.csv',
                  ['Month', 'Started', 'Dropped', 'Net'],
                  trendMonthly.map((t) => [t.period, t.started, t.dropped, t.net]),
                )
              }
            >
              Export enrollment CSV
            </Button>
          ) : undefined
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Active students" value={snapshot.active} hint="Enrolled, not ended" />
          <StatCard label="Waitlisted" value={snapshot.waitlisted} />
          <StatCard label="Dropped (all time in window)" value={snapshot.dropped} />
          <StatCard
            label="Retention (window)"
            value={
              retention && retention.baseActive > 0 ? formatRate(retention.retentionRate) : '—'
            }
            hint={
              retention && retention.baseActive > 0
                ? `${retention.dropped} of ${retention.baseActive} churned`
                : 'Not enough data yet'
            }
          />
        </div>
        {enrollments.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : trendMonthly.length === 0 ? (
          <NotEnoughData what="enrollment activity" />
        ) : (
          <SimpleTable
            columns={['Month', 'Started', 'Dropped', 'Net']}
            rows={trendMonthly.map((t) => [
              t.period,
              <Num key="s" value={t.started} />,
              <Num key="d" value={t.dropped} />,
              <Badge key="n" tone={t.net >= 0 ? 'success' : 'warn'}>
                {t.net >= 0 ? `+${t.net}` : t.net}
              </Badge>,
            ])}
          />
        )}
      </Section>

      {/* Revenue / AR --------------------------------------------------- */}
      <Section
        id="revenue"
        title="Revenue & accounts receivable"
        description="Gross charges and collected payments in the window, plus a point-in-time receivable aging from authoritative account balances."
        action={
          aging.some((b) => b.count > 0) ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                exportCsv(
                  'ar-aging.csv',
                  ['Bucket', 'Amount (cents)', 'Charges'],
                  aging.map((b) => [b.label, b.amountCents, b.count]),
                )
              }
            >
              Export aging CSV
            </Button>
          ) : undefined
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Gross charged"
            value={formatCents(revenue.grossChargedCents)}
            hint={`${revenue.chargeCount} charges`}
          />
          <StatCard
            label="Collected"
            value={formatCents(revenue.collectedCents)}
            hint={`${revenue.paymentCount} payments`}
          />
          <StatCard
            label="Outstanding (now)"
            value={formatCents(ar.outstandingCents)}
            hint={`${ar.familiesWithBalance} accounts owe`}
          />
          <StatCard
            label="Overdue charges (now)"
            value={ar.overdueCount}
            hint={`${ar.openCount} open total`}
          />
        </div>
        {balances.isLoading || charges.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : aging.every((b) => b.count === 0) ? (
          <NotEnoughData what="open receivables" />
        ) : (
          <SimpleTable
            columns={['Aging bucket', 'Amount', 'Charges']}
            rows={aging.map((b) => [
              b.label,
              formatCents(b.amountCents),
              <Num key="c" value={b.count} />,
            ])}
          />
        )}
      </Section>

      {/* Attendance ----------------------------------------------------- */}
      <Section
        id="attendance"
        title="Attendance"
        description="Overall show-up rate plus classes with repeat absences and students trending at-risk in the window."
        action={
          atRisk.length > 0 ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                exportCsv(
                  'at-risk-students.csv',
                  ['Student', 'Absences', 'Sessions', 'Absence rate'],
                  atRisk.map((s) => [s.label, s.absences, s.total, formatRate(s.absenceRate)]),
                )
              }
            >
              Export at-risk CSV
            </Button>
          ) : undefined
        }
      >
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Attendance rate"
            value={attRate.total > 0 ? formatRate(attRate.rate) : '—'}
            {...(attRate.total > 0
              ? { hint: `${attRate.present} present / ${attRate.absent} absent` }
              : {})}
          />
          <StatCard label="Excused" value={attRate.excused} />
          <StatCard label="Repeat-absence classes" value={repeatAbsence.length} />
          <StatCard label="At-risk students" value={atRisk.length} />
        </div>

        {attendance.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : attRate.total === 0 ? (
          <NotEnoughData what="attendance" />
        ) : (
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-2">
              <h3 className="font-medium text-[var(--text-sm)]">Repeat-absence classes</h3>
              {repeatAbsence.length === 0 ? (
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  No class has two or more absences in this window.
                </p>
              ) : (
                <SimpleTable
                  columns={['Class', 'Absences', 'Rate']}
                  rows={repeatAbsence
                    .slice(0, 10)
                    .map((g) => [
                      g.label,
                      <Num key="a" value={g.absences} />,
                      formatRate(g.absenceRate),
                    ])}
                />
              )}
            </div>
            <div className="space-y-2">
              <h3 className="font-medium text-[var(--text-sm)]">At-risk students</h3>
              {atRisk.length === 0 ? (
                <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                  No student is above the at-risk absence threshold.
                </p>
              ) : (
                <SimpleTable
                  columns={['Student', 'Absences', 'Rate']}
                  rows={atRisk.slice(0, 10).map((s) => [
                    s.label,
                    <Num key="a" value={s.absences} />,
                    <Badge key="r" tone="warn">
                      {formatRate(s.absenceRate)}
                    </Badge>,
                  ])}
                />
              )}
            </div>
          </div>
        )}
      </Section>

      {/* Trial funnel --------------------------------------------------- */}
      <Section
        id="funnel"
        title="Trial funnel"
        description="Leads through scheduled trials, attended trials, and conversions to enrolled families in the window."
      >
        {leads.isLoading || trials.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : funnel.leads === 0 && funnel.trialsScheduled === 0 ? (
          <NotEnoughData what="lead or trial activity" />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard label="Leads" value={funnel.leads} />
              <StatCard label="Trials scheduled" value={funnel.trialsScheduled} />
              <StatCard label="Trials attended" value={funnel.trialsAttended} />
              <StatCard
                label="Converted"
                value={funnel.converted}
                hint={`${formatRate(funnel.leadToConvertedRate)} of leads`}
              />
            </div>
            <SimpleTable
              columns={['Stage', 'Count', 'Conversion']}
              rows={[
                ['Leads', <Num key="l" value={funnel.leads} />, '—'],
                ['Trials scheduled', <Num key="ts" value={funnel.trialsScheduled} />, '—'],
                ['Trials attended', <Num key="ta" value={funnel.trialsAttended} />, '—'],
                [
                  'Converted (enrolled)',
                  <Num key="cv" value={funnel.converted} />,
                  <Badge key="rate" tone="success">
                    {formatRate(funnel.attendedToConvertedRate)} of attended
                  </Badge>,
                ],
              ]}
            />
          </>
        )}
      </Section>

      {/* Recital ops ---------------------------------------------------- */}
      <Section
        id="recital"
        title="Recital operations"
        description="Dances that still have no cast assigned across active productions."
        action={
          missingCast.length > 0 ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() =>
                exportCsv(
                  'dances-missing-cast.csv',
                  ['Dance'],
                  missingCast.map((d) => [d.dance_name]),
                )
              }
            >
              Export missing-cast CSV
            </Button>
          ) : undefined
        }
      >
        {danceCast.isLoading ? (
          <p aria-live="polite">Loading…</p>
        ) : (danceCast.data ?? []).length === 0 ? (
          <NotEnoughData what="recital dance data" />
        ) : missingCast.length === 0 ? (
          <Empty
            title="Every dance has a cast"
            description="All active dances have at least one dancer assigned. Lineup conflicts are reviewed per-production on the conflicts tab."
          />
        ) : (
          <SimpleTable
            columns={['Dance missing cast']}
            rows={missingCast.map((d) => [d.dance_name])}
          />
        )}
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Date filter control
// ---------------------------------------------------------------------------

function DateFilter({
  range,
  onChange,
}: {
  range: DateRange;
  onChange: (next: DateRange) => void;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="flex gap-2">
        {PRESETS.map((p) => (
          <Button
            key={p.label}
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange(rangeForDays(p.days))}
          >
            {p.label}
          </Button>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <div>
          <Label htmlFor="range-start">From</Label>
          <Input
            id="range-start"
            type="date"
            value={range.start ?? ''}
            max={range.end}
            onChange={(e) => {
              const next: DateRange = { ...range };
              if (e.target.value) next.start = e.target.value;
              else delete next.start;
              onChange(next);
            }}
          />
        </div>
        <div>
          <Label htmlFor="range-end">To</Label>
          <Input
            id="range-end"
            type="date"
            value={range.end ?? ''}
            min={range.start}
            onChange={(e) => {
              const next: DateRange = { ...range };
              if (e.target.value) next.end = e.target.value;
              else delete next.end;
              onChange(next);
            }}
          />
        </div>
      </div>
    </div>
  );
}
